import { gql, useMutation } from '@apollo/client';
import { useOffsetPaginatedQuery } from '@everlutionsk/ui-apollo';
import { Stack, Typography } from '@mui/material';
import { StatusBadge } from '../components/coreComponents/badge';
import React from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { Table } from '../components/microLayouts/table';
import { useIdentity } from '../identityContext';

// Lists every admin action awaiting a second-admin confirmation click — across
// all kinds (account soft-delete / purge backup / admin-credential grant).
// Each row routes to the kind-specific confirm page. Superadmins can also
// Reject a request inline (declines it + frees a re-request).
export function PendingActionsPage() {
  const navigate = useNavigate();
  const identity = useIdentity();
  const [reject, { loading: rejecting }] = useMutation(rejectMutation, {
    refetchQueries: ['PendingAdminActionMany']
  });

  const { data, pagination, loading } = useOffsetPaginatedQuery(query, {
    variables: page => ({ input: { page } }),
    total: data => data.pendingAdminActionMany.total
  });

  const items = data?.pendingAdminActionMany.items ?? [];

  // Route per role: recipients land on the confirm page (they can actually
  // approve); others land on the originating page where they can still
  // cancel/resend their own request.
  const routeFor = (row: Row): string => {
    if (row.kind === 'adminCredentialsCreate' || row.kind === 'adminCredentialsRevoke') {
      if (identity.isActionConfirmationRecipient && row.token != null) {
        return `/confirm-admin-credentials/${row.token}`;
      }
      return row.adminUserId != null
        ? `/administrators/${row.adminUserId}/credentials`
        : '/administrators';
    }
    if (identity.isActionConfirmationRecipient && row.token != null) {
      return `/confirm-deletion/${row.token}`;
    }
    if (row.kind === 'accountSoftDelete') return `/accounts/${row.accountId}/overview`;
    return `/recovery/${row.accountId}`;
  };

  const onReject = async (row: Row) => {
    const reason = window.prompt('Reason for rejecting (optional):') ?? undefined;
    await reject({ variables: { id: row.id, reason } });
  };

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Pending actions"
        description="Admin requests awaiting a second-admin confirmation click"
      />

      <Table
        items={loading ? undefined : items}
        pagination={pagination}
        rowKey={row => row.id}
        rowProps={row => ({
          onClick: () => navigate(routeFor(row)),
          sx: { cursor: 'pointer' }
        })}
        columns={[
          {
            label: 'Kind',
            render: row => <KindChip kind={row.kind} />
          },
          {
            label: 'Subject',
            render: row => <Subject row={row} />
          },
          {
            label: 'Initiated by',
            render: row => <Typography variant="body2">{row.initiatedByEmail}</Typography>
          },
          {
            label: 'Created',
            render: row => (
              <Typography variant="body2">{new Date(row.createdAt).toLocaleString()}</Typography>
            )
          },
          {
            label: 'Expires',
            render: row => (
              <Typography variant="body2">{new Date(row.expiresAt).toLocaleString()}</Typography>
            )
          },
          {
            label: '',
            render: row => (
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="primary"
                  onClick={event => {
                    event.stopPropagation();
                    navigate(routeFor(row));
                  }}
                >
                  Review
                </Button>
                <Button
                  variant="destructive"
                  disabled={rejecting}
                  onClick={event => {
                    event.stopPropagation();
                    void onReject(row);
                  }}
                >
                  Reject
                </Button>
              </Stack>
            )
          }
        ]}
        noDataPlaceholder="No pending actions."
      />
    </MainWrapper>
  );
}

interface Row {
  readonly id: string;
  readonly kind:
    | 'accountSoftDelete'
    | 'accountPurgeBackup'
    | 'adminCredentialsCreate'
    | 'adminCredentialsRevoke';
  readonly token?: string | null;
  readonly accountId?: string | null;
  readonly accountAlias?: string | null;
  readonly adminUserId?: string | null;
  readonly adminEmail?: string | null;
  readonly adminName?: string | null;
  readonly initiatedByEmail: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

function Subject({ row }: { row: Row }) {
  if (row.kind === 'adminCredentialsCreate' || row.kind === 'adminCredentialsRevoke') {
    return (
      <Stack>
        <Typography variant="body2">{row.adminName || '—'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.adminEmail}
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack>
      <Typography variant="body2">{row.accountAlias ?? '—'}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {row.accountId}
      </Typography>
    </Stack>
  );
}

function KindChip({ kind }: { readonly kind: Row['kind'] }) {
  if (kind === 'accountSoftDelete') return <StatusBadge tone="warning" label="Soft delete" />;
  if (kind === 'accountPurgeBackup') return <StatusBadge tone="error" label="Purge backup" />;
  if (kind === 'adminCredentialsRevoke') return <StatusBadge tone="error" label="Admin revoke" />;
  return <StatusBadge tone="info" label="Admin credentials" />;
}

const query = gql<PendingAdminActionManyGQL>`
  query PendingAdminActionMany($input: PendingAdminActionManyInput) {
    pendingAdminActionMany(input: $input) {
      total
      items {
        id
        kind
        token
        accountId
        accountAlias
        adminUserId
        adminEmail
        adminName
        adminRole
        initiatedByEmail
        createdAt
        expiresAt
        status
      }
    }
  }
`;

const rejectMutation = gql<PendingAdminActionRejectGQL>`
  mutation PendingAdminActionReject($id: ID!, $reason: String) {
    pendingAdminActionReject(id: $id, reason: $reason) {
      id
      status
    }
  }
`;
